/**
 * Schema definitions for the docs-sync scan-phase audit report.
 *
 * The report is the durable output of Phase 1 (Scan) of the docs-sync
 * pipeline (parent epic #448). Phase 1 walks the repo, runs every
 * registered drift check, and persists a single combined report to
 * `.state/docs-sync/<N>-audit.json` (where `<N>` is the
 * tracking-issue number). Phase 2 (Fix) reads the persisted report
 * and applies mechanical fixes — the two phases never share an
 * in-memory handle, so the JSON shape on disk is the contract
 * between them.
 *
 * #518 ships the **schema and the orchestrator skeleton only**. The
 * actual drift checks (API/TSDoc/reference, link, fenced-sample) are
 * wired in by children #519 and #520 by passing additional
 * `AuditCheckRunner` instances to {@link runScan}. An empty
 * runner list is the supported scaffold-time configuration and
 * produces a schema-compliant report with empty findings in every
 * category.
 *
 * The schema is intentionally **keyed by drift-check category** at
 * the top level (`apiDiff`, `tsdocCoverage`, `referenceMismatches`,
 * `linkFailures`, `sampleFailures`) so consumers can render or
 * filter by category without re-grouping a flat list. Each
 * category-keyed array carries category-specific finding shapes,
 * and a flat `findings` array re-aggregates the same records with
 * `category` and `severity` fields attached so the report doubles
 * as a backlog source for downstream tooling.
 */

/**
 * Drift-check categories the docs-sync scan phase can produce
 * findings in. Exhaustive — adding a new category here is a schema
 * change that requires bumping {@link DOCS_SYNC_AUDIT_SCHEMA_VERSION}
 * and updating every consumer that switches on the literal.
 */
export const AuditCategory = {
  /**
   * Findings produced by the public-API diff check that surfaces
   * added, renamed, or removed exports between the current branch
   * and the recorded baseline. Wired in by child issue #519.
   */
  ApiDiff: "apiDiff",

  /**
   * Findings produced by the TSDoc-coverage check that flags public
   * exports without a one-sentence summary. Wired in by child issue
   * #519.
   */
  TsdocCoverage: "tsdocCoverage",

  /**
   * Findings produced by the doc-reference cross-index check that
   * flags inline-code symbol mentions whose target is missing or
   * has changed. Wired in by child issue #519.
   */
  ReferenceMismatches: "referenceMismatches",

  /**
   * Findings produced by the link-integrity check (internal via
   * `astro check`, external via `lychee`). Wired in by child issue
   * #520.
   */
  LinkFailures: "linkFailures",

  /**
   * Findings produced by the fenced-sample compilation check that
   * flags `ts`, `typescript`, and `tsx` fenced code blocks in the
   * docs that fail `tsc`. Wired in by child issue #520.
   */
  SampleFailures: "sampleFailures",
} as const;

/**
 * String-literal type matching the values of {@link AuditCategory}.
 */
export type AuditCategory = (typeof AuditCategory)[keyof typeof AuditCategory];

/**
 * Severity ladder applied to every finding before persistence. The
 * ladder mirrors the parent epic's behavior-on-drift contract —
 * mechanical fixes auto-apply, advisory findings post a non-blocking
 * note, blocking findings hand off to `pr-reviewer` via
 * `review:human-required`.
 */
export const AuditSeverity = {
  /**
   * The finding has a deterministic mechanical fix that the Phase 2
   * (Fix) agent will auto-apply (TSDoc stubs, dead-link updates,
   * registry sync, etc.).
   */
  Mechanical: "mechanical",

  /**
   * The finding deserves human attention but does not block the PR.
   * Surfaced as a non-blocking note on the sticky `## Docs sync`
   * comment.
   */
  Advisory: "advisory",

  /**
   * The finding blocks the PR. Per the parent epic this is reserved
   * for the two narrow cases of confirmed broken external links and
   * fenced TS samples that fail to compile.
   */
  Blocking: "blocking",
} as const;

/**
 * String-literal type matching the values of {@link AuditSeverity}.
 */
export type AuditSeverity = (typeof AuditSeverity)[keyof typeof AuditSeverity];

/**
 * Invocation mode of the scan phase. Determines how downstream
 * tooling treats the report — `pr` reports run against a single
 * branch and feed `pr-reviewer`; `audit` reports walk the whole
 * repo and feed the prioritized `docs:write` backlog.
 */
export const AuditMode = {
  Pr: "pr",
  Audit: "audit",
} as const;

/**
 * String-literal type matching the values of {@link AuditMode}.
 */
export type AuditMode = (typeof AuditMode)[keyof typeof AuditMode];

/**
 * File-and-line location attached to most finding shapes. The
 * `file` field is always a path relative to the repo root so reports
 * are portable across machines; absolute paths must be normalized
 * before they reach the report.
 */
export interface AuditLocation {
  /**
   * Path relative to {@link AuditReport.repoRoot}. Empty string is
   * permitted for findings that have no associated file (e.g. an
   * `apiDiff` removed-export finding that lost its source location).
   */
  readonly file: string;

  /**
   * 1-indexed line number when known. `0` means "line is not known"
   * — downstream consumers must treat `0` as missing rather than as
   * the literal first line.
   */
  readonly line: number;
}

/**
 * Common fields every finding shape carries. The category-specific
 * record types below extend this interface with additional fields.
 */
export interface AuditFindingBase {
  /**
   * Drift-check category that produced the finding. Mirrors the key
   * on the {@link AuditReport} that the finding is filed under.
   */
  readonly category: AuditCategory;

  /**
   * Severity classification per the {@link AuditSeverity} ladder.
   */
  readonly severity: AuditSeverity;

  /**
   * Source location the finding is anchored at. Empty location is
   * tolerated for findings with no concrete anchor.
   */
  readonly location: AuditLocation;

  /**
   * Stable subject identifier. For symbol findings this is the
   * exported symbol name; for link findings the URL; for sample
   * findings the docPath plus fence index. Used by Phase 2 to
   * deduplicate findings across runs.
   */
  readonly subject: string;

  /**
   * One-sentence human-readable description of the finding.
   * Phase 2 surfaces this directly in the sticky PR comment, so
   * keep it short and self-contained — no markdown links to
   * external context, and never includes generated prose over the
   * 100-word stub budget.
   */
  readonly details: string;

  /**
   * Optional hint for the Phase 2 fix agent. When present and the
   * severity is `mechanical`, Phase 2 may use this string to
   * compute the auto-applied edit. Free-form; downstream consumers
   * should treat unknown hints as no-op.
   */
  readonly fixHint?: string;
}

/**
 * Finding produced by the public-API diff check. Wired in by #519.
 */
export interface ApiDiffFinding extends AuditFindingBase {
  readonly category: typeof AuditCategory.ApiDiff;

  /**
   * Kind of API change observed. The literal `added` covers a new
   * export; `removed` covers a deletion; `changed` covers a
   * signature change that does not rename or remove the symbol.
   */
  readonly change: "added" | "removed" | "changed";

  /**
   * Public name of the symbol whose surface changed.
   */
  readonly symbol: string;
}

/**
 * Finding produced by the TSDoc-coverage check. Wired in by #519.
 */
export interface TsdocCoverageFinding extends AuditFindingBase {
  readonly category: typeof AuditCategory.TsdocCoverage;

  /**
   * Public name of the symbol that is missing or has thin TSDoc.
   */
  readonly symbol: string;

  /**
   * Coverage shortfall the finding reports. `missing-summary`
   * means no TSDoc at all; `thin-summary` means the summary exists
   * but is shorter than the configured threshold; `missing-params`
   * / `missing-returns` mean the signature has parameters or a
   * return value with no matching block tag.
   */
  readonly shortfall:
    "missing-summary" | "thin-summary" | "missing-params" | "missing-returns";
}

/**
 * Finding produced by the doc-reference cross-index check. Wired in
 * by #519.
 */
export interface ReferenceMismatchFinding extends AuditFindingBase {
  readonly category: typeof AuditCategory.ReferenceMismatches;

  /**
   * Inline-code symbol referenced in the markdown source.
   */
  readonly symbol: string;

  /**
   * Why the reference is flagged. `unknown-symbol` means the
   * symbol does not appear in the supplied known-symbols set;
   * `signature-changed` means the symbol still resolves but its
   * type signature differs from the cached snapshot.
   */
  readonly mismatch: "unknown-symbol" | "signature-changed";
}

/**
 * Finding produced by the link-integrity check. Wired in by #520.
 */
export interface LinkFailureFinding extends AuditFindingBase {
  readonly category: typeof AuditCategory.LinkFailures;

  /**
   * URL or relative path that failed integrity checks.
   */
  readonly url: string;

  /**
   * Whether the link is internal (resolved by `astro check`) or
   * external (resolved by `lychee`).
   */
  readonly kind: "internal" | "external";

  /**
   * Short tool-supplied diagnostic explaining the failure.
   */
  readonly reason: string;
}

/**
 * Finding produced by the fenced-sample compilation check. Wired in
 * by #520.
 */
export interface SampleFailureFinding extends AuditFindingBase {
  readonly category: typeof AuditCategory.SampleFailures;

  /**
   * 0-indexed position of the fenced block within the markdown file.
   */
  readonly fenceIndex: number;

  /**
   * Language tag the sample carried (e.g. `ts`, `typescript`,
   * `tsx`, `typescriptreact`).
   */
  readonly lang: string;

  /**
   * Compiler diagnostics produced by `tsc` against the sample.
   */
  readonly diagnostics: ReadonlyArray<string>;
}

/**
 * Discriminated union of every category-specific finding shape.
 */
export type AuditFinding =
  | ApiDiffFinding
  | TsdocCoverageFinding
  | ReferenceMismatchFinding
  | LinkFailureFinding
  | SampleFailureFinding;

/**
 * Schema version embedded on every persisted audit report. Bump on
 * any breaking change to the report shape so downstream consumers
 * can refuse incompatible files rather than mis-parsing them.
 *
 * Versioning is **integer-monotonic**: the producer of a v2 report
 * is responsible for keeping the v1 fields parseable when
 * possible, but a v1 reader must reject a v2 report it does not
 * understand.
 */
export const DOCS_SYNC_AUDIT_SCHEMA_VERSION = 1;

/**
 * Top-level audit-report shape persisted to
 * `.state/docs-sync/<N>-audit.json`. The category-keyed
 * arrays are the durable surface; the flat `findings` array is a
 * convenience aggregation of the same records.
 */
export interface AuditReport {
  /**
   * Schema version embedded on every persisted report. Always
   * equal to {@link DOCS_SYNC_AUDIT_SCHEMA_VERSION} at write time.
   */
  readonly schemaVersion: number;

  /**
   * Tracking-issue number the scan ran for. Determines the
   * filename when persisted (`<issueNumber>-audit.json`).
   */
  readonly issueNumber: number;

  /**
   * Invocation mode — `pr` for diff-scoped runs invoked through
   * `/docs-sync-pr`, `audit` for full-repo runs invoked through
   * `/docs-sync-audit`.
   */
  readonly mode: AuditMode;

  /**
   * ISO 8601 UTC timestamp the scan completed at. Always written
   * with `Z` suffix; consumers parse with `Date(...)`.
   */
  readonly generatedAt: string;

  /**
   * Absolute path to the repository root the scan ran against.
   * Persisted so a downstream consumer reading the report on a
   * different checkout can rewrite paths relative to its own
   * root.
   */
  readonly repoRoot: string;

  /**
   * Optional free-form scope label attached at the issue level
   * (e.g. `pr:feat/518-foo`, `audit:packages/configulator`).
   * Empty string when no scope was supplied.
   */
  readonly scope: string;

  /**
   * Findings grouped by drift-check category. Every category key
   * is always present and always an array — empty arrays are
   * persisted explicitly so consumers never have to test for
   * presence.
   */
  readonly categories: {
    readonly apiDiff: ReadonlyArray<ApiDiffFinding>;
    readonly tsdocCoverage: ReadonlyArray<TsdocCoverageFinding>;
    readonly referenceMismatches: ReadonlyArray<ReferenceMismatchFinding>;
    readonly linkFailures: ReadonlyArray<LinkFailureFinding>;
    readonly sampleFailures: ReadonlyArray<SampleFailureFinding>;
  };

  /**
   * Flat re-aggregation of every category-keyed finding into a
   * single deterministically-ordered array. Sorted by `severity`
   * (`blocking` first, then `advisory`, then `mechanical`), then
   * `category` (alphabetical), then `location.file`,
   * `location.line`, `subject`. Provided so consumers that don't
   * need the category grouping can iterate one array and see the
   * merge-blocking findings at the top.
   */
  readonly findings: ReadonlyArray<AuditFinding>;
}

/**
 * JSON-Schema-shaped descriptor for {@link AuditReport}. Returned by
 * {@link auditReportJsonSchema} so downstream tooling (e.g. ajv)
 * can validate persisted reports without re-importing the TS
 * types. The shape is hand-authored and deliberately permissive on
 * forward-compatible additions (extra category keys are forbidden;
 * extra finding fields are permitted).
 *
 * Returned as a plain JSON-shaped record — callers that want to
 * feed it into ajv or another validator pass it through directly.
 */
export function auditReportJsonSchema(): Record<string, unknown> {
  const locationSchema = {
    type: "object",
    additionalProperties: false,
    required: ["file", "line"],
    properties: {
      file: { type: "string" },
      line: { type: "integer", minimum: 0 },
    },
  };

  const findingBaseProperties = {
    severity: { type: "string", enum: ["mechanical", "advisory", "blocking"] },
    location: locationSchema,
    subject: { type: "string" },
    details: { type: "string" },
    fixHint: { type: "string" },
  };

  const apiDiffFindingSchema = {
    type: "object",
    required: [
      "category",
      "severity",
      "location",
      "subject",
      "details",
      "change",
      "symbol",
    ],
    properties: {
      ...findingBaseProperties,
      category: { type: "string", const: "apiDiff" },
      change: { type: "string", enum: ["added", "removed", "changed"] },
      symbol: { type: "string" },
    },
  };

  const tsdocCoverageFindingSchema = {
    type: "object",
    required: [
      "category",
      "severity",
      "location",
      "subject",
      "details",
      "shortfall",
      "symbol",
    ],
    properties: {
      ...findingBaseProperties,
      category: { type: "string", const: "tsdocCoverage" },
      shortfall: {
        type: "string",
        enum: [
          "missing-summary",
          "thin-summary",
          "missing-params",
          "missing-returns",
        ],
      },
      symbol: { type: "string" },
    },
  };

  const referenceMismatchFindingSchema = {
    type: "object",
    required: [
      "category",
      "severity",
      "location",
      "subject",
      "details",
      "mismatch",
      "symbol",
    ],
    properties: {
      ...findingBaseProperties,
      category: { type: "string", const: "referenceMismatches" },
      mismatch: {
        type: "string",
        enum: ["unknown-symbol", "signature-changed"],
      },
      symbol: { type: "string" },
    },
  };

  const linkFailureFindingSchema = {
    type: "object",
    required: [
      "category",
      "severity",
      "location",
      "subject",
      "details",
      "url",
      "kind",
      "reason",
    ],
    properties: {
      ...findingBaseProperties,
      category: { type: "string", const: "linkFailures" },
      url: { type: "string" },
      kind: { type: "string", enum: ["internal", "external"] },
      reason: { type: "string" },
    },
  };

  const sampleFailureFindingSchema = {
    type: "object",
    required: [
      "category",
      "severity",
      "location",
      "subject",
      "details",
      "fenceIndex",
      "lang",
      "diagnostics",
    ],
    properties: {
      ...findingBaseProperties,
      category: { type: "string", const: "sampleFailures" },
      fenceIndex: { type: "integer", minimum: 0 },
      lang: { type: "string" },
      diagnostics: { type: "array", items: { type: "string" } },
    },
  };

  return {
    $schema: "http://json-schema.org/draft-07/schema#",
    title: "DocsSyncAuditReport",
    type: "object",
    additionalProperties: false,
    required: [
      "schemaVersion",
      "issueNumber",
      "mode",
      "generatedAt",
      "repoRoot",
      "scope",
      "categories",
      "findings",
    ],
    properties: {
      schemaVersion: { type: "integer", const: DOCS_SYNC_AUDIT_SCHEMA_VERSION },
      issueNumber: { type: "integer", minimum: 0 },
      mode: { type: "string", enum: ["pr", "audit"] },
      generatedAt: { type: "string", format: "date-time" },
      repoRoot: { type: "string" },
      scope: { type: "string" },
      categories: {
        type: "object",
        additionalProperties: false,
        required: [
          "apiDiff",
          "tsdocCoverage",
          "referenceMismatches",
          "linkFailures",
          "sampleFailures",
        ],
        properties: {
          apiDiff: { type: "array", items: apiDiffFindingSchema },
          tsdocCoverage: { type: "array", items: tsdocCoverageFindingSchema },
          referenceMismatches: {
            type: "array",
            items: referenceMismatchFindingSchema,
          },
          linkFailures: { type: "array", items: linkFailureFindingSchema },
          sampleFailures: { type: "array", items: sampleFailureFindingSchema },
        },
      },
      findings: {
        type: "array",
        items: {
          oneOf: [
            apiDiffFindingSchema,
            tsdocCoverageFindingSchema,
            referenceMismatchFindingSchema,
            linkFailureFindingSchema,
            sampleFailureFindingSchema,
          ],
        },
      },
    },
  };
}
